import { TableScroll } from '@web/components/table-scroll.tsx';
import { quoteValueAmount } from '@web/components/quote/plain-price.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState } from '@web/components/ui.tsx';
import { CHART_SERIES, ChartLegend, WeeklyTrendChart } from '@web/components/reports/charts.tsx';
import { formatMinutes } from '@web/i18n/format.ts';
import type { QuoteFunnelReport, QuoteFunnelStalledRow, QuoteValueSum } from '@shared/types/index.ts';
import {
  reportRetry,
  formatDay,
  useReportDate,
  StatTile,
  ReportBoundary,
  SectionCard,
  TH,
  THR,
  TD,
  TDR,
  ROW,
  type RangeParams,
} from '../shared.tsx';

const FUNNEL_BUCKETS = [
  'drafted',
  'sent',
  'accepted',
  'declined',
  'viewedPending',
  'unviewedPending',
  'expired',
  'closedOther',
] as const;

export function FunnelSection({ range, scope }: { range: RangeParams; scope?: 'all' }) {
  const { t } = useTranslation();
  const paging = useReportPaging({ ...range, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'funnel', { ...range, scope, pages }, paging.identity],
    queryFn: () => reportsApi.quoteFunnel({ ...range, scope, pages }),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        // A range quote counts as its band, never its stored middle (plain-price.tsx).
        const amount = (sum: QuoteValueSum) => quoteValueAmount(sum, report.currencyCode, t);
        const hours = (value: number | null) => (value === null ? '—' : formatMinutes(Math.round(value * 60)));
        const decided = report.accepted.count + report.declined.count;
        const acceptanceRate = decided > 0 ? Math.round((report.accepted.count / decided) * 100) : null;
        const awaiting = report.viewedPending.count + report.unviewedPending.count;
        const trendSeries = [
          { name: t('reports.funnel.seriesSent'), color: CHART_SERIES[0], values: report.weekly.map((w) => w.sent) },
          {
            name: t('reports.funnel.seriesAccepted'),
            color: CHART_SERIES[1],
            values: report.weekly.map((w) => w.accepted),
          },
          {
            name: t('reports.funnel.seriesDeclined'),
            color: CHART_SERIES[2],
            values: report.weekly.map((w) => w.declined),
          },
        ];

        // The stall lists are today's follow-up queue, deliberately not
        // range-bound — a quiet recent range must not hide older quotes that
        // still need a call.
        if (
          report.drafted.count === 0 &&
          report.sent.count === 0 &&
          report.sentNotViewed.length === 0 &&
          report.viewedNoAnswer.length === 0 &&
          (report.pagination?.sentNotViewed?.page ?? 1) === 1 &&
          (report.pagination?.viewedNoAnswer?.page ?? 1) === 1
        ) {
          return <EmptyState title={t('reports.funnel.emptyTitle')}>{t('reports.funnel.emptyBody')}</EmptyState>;
        }

        return (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label={t('reports.funnel.sent')}
                value={String(report.sent.count)}
                hint={t('reports.funnel.sentHint', { value: amount(report.sent) })}
              />
              <StatTile
                label={t('reports.funnel.accepted')}
                value={acceptanceRate === null ? String(report.accepted.count) : `${acceptanceRate}%`}
                hint={t('reports.funnel.acceptedHint', {
                  count: report.accepted.count,
                  value: amount(report.accepted),
                })}
              />
              <StatTile
                label={t('reports.funnel.awaiting')}
                value={String(awaiting)}
                hint={t('reports.funnel.awaitingHint', {
                  value: amount({
                    totalCents: report.viewedPending.totalCents + report.unviewedPending.totalCents,
                    lowCents: report.viewedPending.lowCents + report.unviewedPending.lowCents,
                    highCents: report.viewedPending.highCents + report.unviewedPending.highCents,
                  }),
                })}
              />
              <StatTile
                label={t('reports.funnel.medianDecision')}
                value={hours(report.medianHoursToDecision)}
                hint={t('reports.funnel.medianDecisionHint', { time: hours(report.medianHoursToView) })}
              />
            </div>

            <div className="space-y-6">
              {report.weekly.length >= 2 ? (
                <SectionCard
                  title={t('reports.funnel.trendTitle')}
                  hint={t('reports.funnel.trendHint')}
                  csvHref={reportsApi.csvUrl('quote-funnel', { ...range, scope })}
                >
                  <ChartLegend items={trendSeries.map(({ name, color }) => ({ name, color }))} />
                  <WeeklyTrendChart
                    labels={report.weekly.map((week) => formatDay(week.weekStart))}
                    series={trendSeries}
                    ariaLabel={t('reports.funnel.trendTitle')}
                  />
                </SectionCard>
              ) : null}

              <SectionCard title={t('reports.funnel.bucketsTitle')} hint={t('reports.funnel.bucketsHint')}>
                <TableScroll aria-label={t('reports.funnel.bucketsTitle')} className="overflow-x-auto">
                  <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-[color:var(--color-line)]">
                        <th scope="col" className={TH}>
                          {t('reports.funnel.colStage')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.funnel.colQuotes')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.funnel.colValue')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {FUNNEL_BUCKETS.map((bucket) => {
                        const entry = (report as QuoteFunnelReport)[bucket];
                        return (
                          <tr key={bucket} className={ROW}>
                            <td className={TD}>{t(`reports.funnel.bucket.${bucket}`)}</td>
                            <td className={TDR}>{entry.count}</td>
                            <td className={TDR}>{amount(entry)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              </SectionCard>

              {/* Only when there is something to show. A company that has
                  lost nothing in the range does not need a table telling it
                  so, and one whose declines all predate the question would
                  get a single "not given" row that reads as a bug. */}
              {report.declineReasons.length > 0 ? (
                <SectionCard
                  title={t('reports.funnel.declineReasonsTitle')}
                  hint={t('reports.funnel.declineReasonsHint')}
                >
                  <TableScroll aria-label={t('reports.funnel.declineReasonsTitle')} className="overflow-x-auto">
                    <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                      <thead>
                        <tr className="border-b border-[color:var(--color-line)]">
                          <th scope="col" className={TH}>
                            {t('reports.funnel.colReason')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.funnel.colQuotes')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.funnel.colValue')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Keyed by position, not by reason: the server maps
                            every reason it does not recognize to null (the
                            list can grow — migration 0076 has no CHECK), so
                            two SQL groups can arrive as two `null` rows and a
                            reason-keyed list would render duplicate keys. */}
                        {report.declineReasons.map((row, index) => (
                          <tr key={`${row.reason ?? 'unspecified'}:${index}`} className={ROW}>
                            <td className={TD}>
                              {row.reason
                                ? t(`reports.funnel.declineReason.${row.reason}`)
                                : t('reports.funnel.declineReason.unspecified')}
                            </td>
                            <td className={TDR}>{row.count}</td>
                            <td className={TDR}>{amount(row)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                </SectionCard>
              ) : null}

              <div className="grid gap-6 lg:grid-cols-2">
                <SectionCard
                  title={t('reports.funnel.sentNotViewedTitle')}
                  collection="sentNotViewed"
                  hint={t('reports.funnel.sentNotViewedHint')}
                >
                  <StalledList rows={report.sentNotViewed} amount={amount} empty={t('reports.funnel.stalledEmpty')} />
                </SectionCard>
                <SectionCard
                  title={t('reports.funnel.viewedNoAnswerTitle')}
                  collection="viewedNoAnswer"
                  hint={t('reports.funnel.viewedNoAnswerHint')}
                >
                  <StalledList rows={report.viewedNoAnswer} amount={amount} empty={t('reports.funnel.stalledEmpty')} />
                </SectionCard>
              </div>
            </div>
          </>
        );
      }}
    </ReportBoundary>
  );
}

function StalledList({
  rows,
  amount,
  empty,
}: {
  rows: QuoteFunnelStalledRow[];
  amount: (sum: QuoteValueSum) => string;
  empty: string;
}) {
  const formatDate = useReportDate();
  const { t } = useTranslation();
  if (rows.length === 0) return <p className="text-[color:var(--color-muted)]">{empty}</p>;
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((row) => (
        <li key={row.quoteId} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <span>
            <Link to={`/quotes/${row.quoteId}`} className="font-medium text-[color:var(--color-brand)] hover:underline">
              {t('reports.funnel.quoteNumber', { number: row.quoteNumber })}
            </Link>{' '}
            <span className="text-[color:var(--color-muted)]">{row.customerName}</span>
          </span>
          <span className="tabular whitespace-nowrap">
            {amount(row)}
            <span className="text-[color:var(--color-muted)]"> · {formatDate(row.sinceAt)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
